import * as React from "react";
import { mergeStyleSets } from "@fluentui/react";
import { OverviewContentSection, OverviewContentSectionTemplate } from "./OverviewContentSectionTemplate";
import {
  OverviewGettingStartedSection,
  OverviewGettingStartedSectionTemplate,
} from "./OverviewGettingStartedSectionTemplate";
import { OverviewInfoBlockSection, OverviewInfoBlockSectionTemplate } from "./OverviewInfoBlockSectionTemplate";
import { OverviewLinkSection, OverviewLinkSectionTemplate } from "./OverviewLinkSectionTemplate";
import {
  OverviewRecentViewResourceSection,
  OverviewRecentViewResourceSectionTemplate,
} from "./OverviewRecentViewResourceSectionTemplate";

export interface OverviewTemplateProps {

  /**
   * TestId of current template
   */
  testId?: string;

  /**
   * Overview info block Section component
   */
  infoBlock?: OverviewInfoBlockSection;

  /**
   * Getting started sections component
   */
  gettingStartedSections?: OverviewGettingStartedSection[];

  /**
   * Overview content sections component
   */
  contentSections?: OverviewContentSection[];

  /**
   * Overview recent view resource section component
   */
  recentViewResourceSection?: OverviewRecentViewResourceSection;

  /**
   * Overview link sections component
   */
  linkSections?: OverviewLinkSection[];
}

const classNames = mergeStyleSets({
  overviewTemplateContainer: {
    padding: "24px 24px",
    fontSize: "13px",
    maxWidth: "1063px",
    margin: "0 auto",
  },
});

/**
 * OverviewTemplate is a component to build the overview page
 *
 */
export const OverviewTemplate = ({
  testId,
  linkSections,
  contentSections,
  recentViewResourceSection,
  infoBlock,
  gettingStartedSections,
}: OverviewTemplateProps): JSX.Element => (
  <div
    id="overview-container"
    className={classNames.overviewTemplateContainer}
    data-test-id={testId}
  >
    <main>
      {infoBlock && (
        <OverviewInfoBlockSectionTemplate
          testId={infoBlock.testId}
          message={infoBlock.message}
          messageType={infoBlock.messageType}
          actionButtons={infoBlock.actionButtons}
          isMultiline={infoBlock.isMultiline}
        />
      )}
      {(gettingStartedSections && gettingStartedSections.length > 0) && (
        <section
          id="overview-getting-started-wrapper"
        >
          {gettingStartedSections.map(
            (section, i) => (
              <OverviewGettingStartedSectionTemplate
                title={section.title}
                key={`getting-started-section-${i.toString()}`}
                testId={section.testId}
                items={section.items}
              />
            ),
          )}
        </section>
      )}
      {(contentSections && contentSections.length > 0) && (
        <section
          id="overview-main-content-wrapper"
        >
          {contentSections.map(
            (section, i) => (
              <OverviewContentSectionTemplate
                title={section.title}
                key={`overview-content-section-${i.toString()}`}
                testId={section.testId}
                items={section.items}
                itemStyle={section.itemStyle}
              />
            ),
          )}
        </section>
      )}
      {recentViewResourceSection && (
        <OverviewRecentViewResourceSectionTemplate
          title={recentViewResourceSection.title}
          items={recentViewResourceSection.items}
          testId={recentViewResourceSection.testId}
          itemsHead={recentViewResourceSection.itemsHead}
        />
      )}
      {(linkSections && linkSections.length > 0) && (
        <section
          id="overview-links-wrapper"
        >
          {linkSections.map(
            (item, i) => (
              <OverviewLinkSectionTemplate
                title={item.title}
                key={`overview-link-section-${i.toString()}`}
                testId={item.testId}
                items={item.items}
              />
            ),
          )}
        </section>
      )}
    </main>
  </div>
);

export {
  OverviewInfoBlockStatus, OverviewInfoBlockSection, OverviewInfoBlockButton,
} from "./OverviewInfoBlockSectionTemplate";
export {
  ItemStyle, OverviewContentItem, OverviewContentSection,
} from "./OverviewContentSectionTemplate";
export {
  buildOverviewGettingStartedItemTestIds,
  OverviewGettingStartedItem,
  OverviewGettingStartedSection,
  OverviewGettingStartedItemTestIds,
} from "./OverviewGettingStartedSectionTemplate";
export { OverviewLinkItem, OverviewLinkSection } from "./OverviewLinkSectionTemplate";
export { RecentViewResourceItem, OverviewRecentViewResourceSection } from "./OverviewRecentViewResourceSectionTemplate";
